import { useEffect } from 'react';
import { toast } from 'sonner';
import { Grid3x3, History, Users } from 'lucide-react';

import { cn } from '@/lib/utils';
import { handleMutationError } from '@/lib/handle-mutation-error';
import { usePermissions } from '@/hooks/use-permissions';
import { PERMISSION } from '@/features/users-roles/constants';
import { useVoiceMinutesStatus } from '@/features/billing/hooks/use-voice-minutes-status';

import { useVoiceSession } from '../../hooks/use-voice-session';
import { useBlockNumber } from '../../hooks/use-blocked-numbers';
import {
    useEndCall,
    useHoldCall,
    useResumeCall,
    useTransferCall,
    useUpdateCallDisposition,
} from '../../hooks/use-call-mutations';
import { useMyNumbers } from '../../hooks/use-my-numbers';
import { useMyVoicePresenceStatus, useSetMyVoicePresence } from '../../hooks/use-voice-presence';
import { CALL_WIDGET_SLOT } from '../../utils/call-widget-target';
import { useRingtone } from '../../hooks/use-ringtone';
import { useWidgetDrag } from '../../hooks/use-widget-drag';
import { useWidgetResize, widgetScale, WIDGET_BASE_WIDTH } from '../../hooks/use-widget-resize';
import { useVoiceStore } from '../../state/voice-ui.store';
import type { IVoiceContactSnapshot } from '../../types';
import { ContactsPanel } from './contacts-panel';
import { DialerPanel } from './dialer-panel';
import { HeldQueue } from './held-queue';
import { HistoryDetailPanel } from './history-detail-panel';
import { HistoryPanel } from './history-panel';
import { InCallKeypadPanel } from './in-call-keypad-panel';
import { InCallPanel } from './in-call-panel';
import { PostCallCard } from './post-call-card';
import { RingingQueue } from './ringing-queue';
import { TransferPanel } from './transfer-panel';
import { TypingFocusScope } from './typing-focus-scope';
import { WidgetHeader } from './widget-header';

const EDGE_MARGIN = 16;

const IDLE_TABS = [
    { id: 'keypad', label: 'Keypad', Icon: Grid3x3 },
    { id: 'contacts', label: 'Contacts', Icon: Users },
    { id: 'history', label: 'History', Icon: History },
] as const;

/**
 * Persistent floating call widget — mounted once in `AppLayout` (see the
 * voice-ui store's own doc comment). Drives itself entirely off
 * `useVoiceSession()` (device + socket) and `useVoiceStore` so it survives
 * route navigation without losing an in-progress call.
 */
export function CallWidget() {
    const { hasAnyPermission, isLoading: permissionsLoading } = usePermissions();
    const canUseVoice = hasAnyPermission([
        PERMISSION.VOICE_CALLS_MAKE,
        PERMISSION.VOICE_CALLS_RECEIVE,
    ]);
    // Only receive-capable users register with Twilio, so only for them does
    // "offline" mean missed calls — make-only users would see a false alarm.
    const canReceive = hasAnyPermission([PERMISSION.VOICE_CALLS_RECEIVE]);
    const canMake = hasAnyPermission([PERMISSION.VOICE_CALLS_MAKE]);

    const {
        dial,
        pickHeldCall,
        answerIncoming,
        declineIncoming,
        hangupLocal,
        toggleMute,
        sendDigits,
    } = useVoiceSession();

    const deviceStatus = useVoiceStore((s) => s.deviceStatus);
    const deviceError = useVoiceStore((s) => s.deviceError);
    const minimized = useVoiceStore((s) => s.minimized);
    const position = useVoiceStore((s) => s.position);
    const size = useVoiceStore((s) => s.size);
    const activePanel = useVoiceStore((s) => s.activePanel);
    const historyDetailTarget = useVoiceStore((s) => s.historyDetailTarget);
    const ringingQueue = useVoiceStore((s) => s.ringingQueue);
    const heldQueue = useVoiceStore((s) => s.heldQueue);
    const activeCall = useVoiceStore((s) => s.activeCall);
    const postCallCardCallId = useVoiceStore((s) => s.postCallCardCallId);
    const isDialing = useVoiceStore((s) => s.isDialing);
    const setIsDialing = useVoiceStore((s) => s.setIsDialing);
    const selectedFromIdentityId = useVoiceStore((s) => s.selectedFromIdentityId);
    const dialRequest = useVoiceStore((s) => s.dialRequest);
    const ringingAction = useVoiceStore((s) => s.ringingAction);
    const setMinimized = useVoiceStore((s) => s.setMinimized);
    const setPosition = useVoiceStore((s) => s.setPosition);
    const setActivePanel = useVoiceStore((s) => s.setActivePanel);
    const openHistoryDetail = useVoiceStore((s) => s.openHistoryDetail);
    const closeHistoryDetail = useVoiceStore((s) => s.closeHistoryDetail);
    const setPostCallCardCallId = useVoiceStore((s) => s.setPostCallCardCallId);
    const clearDialRequest = useVoiceStore((s) => s.clearDialRequest);
    const clearRingingAction = useVoiceStore((s) => s.clearRingingAction);

    // Client-side minute gate — mirrors the backend reserve so we can block a
    // doomed dial before the 50002 round-trip. Only a hard block (limited plan,
    // zero remaining) disables dialing; unlimited/near-limit/unknown never do.
    const voiceMinutes = useVoiceMinutesStatus();
    const dialBlocked = canMake && voiceMinutes.isExhausted;

    // Offline means unavailable in both directions — inbound already skips this
    // agent, so outbound is blocked to match (the backend rejects with 96004
    // regardless; this is the instant feedback). AWAY/BUSY still dial. The hook
    // defaults to ACTIVE when the org snapshot is unreadable, so an agent
    // without VOICE_CALLS_READ is never blocked on a guess.
    const myPresence = useMyVoicePresenceStatus();
    const setPresence = useSetMyVoicePresence();
    const offlineBlocked = canMake && myPresence === 'OFFLINE';
    // `GET /voice/numbers/mine` is already the voice-enabled numbers this agent
    // may call from (the ContactDialer rule): an empty answer means there is
    // nothing to dial out on. Only a loaded answer blocks — never loading/error.
    const myNumbers = useMyNumbers({ enabled: canMake });
    const noNumberBlocked = canMake && myNumbers.isSuccess && myNumbers.data.length === 0;
    const dialDisabledReason = noNumberBlocked
        ? 'No active voice number — activate a number in Settings → Voice to make calls'
        : offlineBlocked
          ? "You're Offline — set yourself Active to make calls"
          : dialBlocked
            ? 'No voice minutes left'
            : null;

    const holdMutation = useHoldCall();
    const resumeMutation = useResumeCall();
    const endMutation = useEndCall();
    const transferMutation = useTransferCall();
    // Owned here (not in PostCallCard) so the note PATCH and the block still
    // toast + invalidate after the card unmounts on answer/dial dismissal.
    const dispositionMutation = useUpdateCallDisposition();
    const blockMutation = useBlockNumber();

    // Header drags skip its buttons — the panel is the only draggable surface
    // now (the minimized launcher is fixed in the app header).
    const headerDrag = useWidgetDrag({ skipInteractive: true });
    const resize = useWidgetResize();

    // Ring while at least one call waits and none is active (the SDK's own
    // single-call ringtone is disabled in voice-device.ts). `ringAudioBlocked`
    // is true when autoplay policy is muting the ring — any gesture unlocks it.
    const ringAudioBlocked = useRingtone(ringingQueue.length > 0 && !activeCall);

    // Defined above the `!canUseVoice` early return so the dial-request effect
    // below can reference it (a `const` arrow is not hoisted).
    const handleDial = async (
        toNumber: string,
        options?: {
            contactId?: string;
            contact?: IVoiceContactSnapshot;
            fromIdentityId?: string;
        },
    ): Promise<void> => {
        // Re-entry guard for callers without their own isDialing check — a
        // double-clicked History row (rows only disable on activeCall, which
        // stays null until initiateCall resolves) would otherwise fire a
        // second dial and orphan the first call server-side in INITIATED.
        if (isDialing) return;
        // Backstop for the dial paths without their own disabled state (history
        // redial, post-call "call again", the out-of-widget ContactDialer) — the
        // manual dialer button is already disabled via `dialDisabledReason`.
        // Offline is checked first: it's the more specific state, and unlike the
        // minute block the agent can clear it themselves right from the toast.
        if (noNumberBlocked) {
            toast.error('No active voice number', {
                description: 'Activate a voice number in Settings → Voice to make calls.',
            });
            return;
        }
        if (offlineBlocked) {
            toast.error("You're Offline", {
                description: 'Set your status to Active to make calls.',
                action: { label: 'Go Active', onClick: () => setPresence.mutate('ACTIVE') },
            });
            return;
        }
        if (dialBlocked) {
            toast.error('No voice minutes left', {
                description: 'Your plan’s voice minutes are used up. Add an add-on to make calls.',
            });
            return;
        }
        setIsDialing(true);
        try {
            await dial(
                {
                    toNumber,
                    // A per-call caller ID (ContactDialer) wins; otherwise the
                    // agent's persisted global choice from the widget header.
                    fromIdentityId: options?.fromIdentityId ?? selectedFromIdentityId ?? undefined,
                    ...(options?.contactId ? { contactId: options.contactId } : {}),
                },
                { ...(options?.contact ? { contact: options.contact } : {}) },
            );
            // The number made it out — drop the dialer draft so reopening the
            // widget starts clean. A failed dial keeps it for a retry.
            useVoiceStore.getState().setDialerDraft('');
        } catch (error) {
            handleMutationError(error, 'Failed to start the call');
        } finally {
            setIsDialing(false);
        }
    };

    // A dial asked for from outside the widget (ContactDialer on a contact card
    // or the inbox header) — `useVoiceSession` is mounted only here, so the
    // store is the bridge. Clear the request BEFORE dialing: a rejected dial
    // would otherwise still be pending and re-fire on the next render.
    useEffect(() => {
        if (!dialRequest) return;
        clearDialRequest();
        if (!canMake) return;
        void handleDial(dialRequest.toNumber, {
            ...(dialRequest.contactId ? { contactId: dialRequest.contactId } : {}),
            ...(dialRequest.contact ? { contact: dialRequest.contact } : {}),
            ...(dialRequest.fromIdentityId ? { fromIdentityId: dialRequest.fromIdentityId } : {}),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- `handleDial` is re-created each render; the request is the trigger
    }, [dialRequest, canMake, clearDialRequest]);

    // An answer/decline asked for from the header badge (`CallHeaderBadge`) —
    // the store is the bridge, mirroring dialRequest above. Clear BEFORE
    // acting so a throwing handler can never re-fire on the next render.
    useEffect(() => {
        if (!ringingAction) return;
        clearRingingAction();
        if (ringingAction.action === 'answer') answerIncoming(ringingAction.callId);
        else declineIncoming(ringingAction.callId);
    }, [ringingAction, clearRingingAction, answerIncoming, declineIncoming]);

    // A restored (persisted) position may be off-screen after a viewport
    // change — clamp it back into view once on mount. `position` only ever
    // places the expanded panel now (the launcher is fixed in the header),
    // so clamp against the panel's width.
    useEffect(() => {
        if (!position) return;
        const maxX = window.innerWidth - WIDGET_BASE_WIDTH - EDGE_MARGIN;
        const maxY = window.innerHeight - 80;
        if (position.x > maxX || position.y > maxY) {
            setPosition({
                x: Math.min(Math.max(position.x, EDGE_MARGIN), maxX),
                y: Math.min(Math.max(position.y, EDGE_MARGIN), maxY),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only viewport clamp
    }, []);

    // Answering a queued call or starting a dial makes a call active, which
    // dismisses the post-call card — the card commits any note draft as it
    // unmounts, so a typed note is never dropped.
    useEffect(() => {
        if (activeCall && postCallCardCallId) setPostCallCardCallId(null);
    }, [activeCall, postCallCardCallId, setPostCallCardCallId]);

    if (permissionsLoading || !canUseVoice) return null;

    // The minimized launcher lives in the app header (`CallHeaderBadge`);
    // while minimized this component renders nothing but keeps the session
    // hooks + ringtone above alive so calls still ring in.
    if (minimized) return null;

    const handleToggleHold = (): void => {
        if (!activeCall) return;
        if (activeCall.onHold) resumeMutation.mutate(activeCall.callId);
        else holdMutation.mutate(activeCall.callId);
    };

    const handleEnd = (): void => {
        if (!activeCall) return;
        hangupLocal();
        if (isDialing && activeCall.status === 'INITIATED') {
            // Optimistic call whose initiate round trip is still in flight —
            // there may be no server row to end yet. Dismiss locally; the
            // resolving dial() finds its entry gone and ends the server call.
            useVoiceStore.getState().setActiveCall(null);
            return;
        }
        const { callId } = activeCall;
        // Answered calls end COMPLETED server-side, the one outcome that earns
        // the wrap-up card (same pair the in-call panel calls "connected").
        const wasAnswered = activeCall.status === 'IN_PROGRESS' || activeCall.status === 'ON_HOLD';
        endMutation.mutate(callId, {
            // The server only answers after marking the call terminal, so the
            // call IS over. Normally `voice:call:ended` has already closed the
            // panel by now; when that event is late or missed, the panel sat on
            // a live-looking call with End call enabled again, so one click
            // never seemed to close it. Settle exactly as the event would — a
            // no-op when the event got here first.
            onSuccess: () => {
                const store = useVoiceStore.getState();
                if (store.activeCall?.callId !== callId) return;
                store.setActiveCall(null);
                if (wasAnswered) store.setPostCallCardCallId(callId);
            },
        });
    };

    const handleTransfer = (target: { targetUserId?: string; targetNumber?: string }): void => {
        if (!activeCall) return;
        transferMutation.mutate(
            { id: activeCall.callId, ...target },
            { onSuccess: () => setActivePanel(null) },
        );
    };

    const positionStyle: React.CSSProperties = position
        ? { left: position.x, top: position.y, right: 'auto', bottom: 'auto' }
        : {};

    const voiceOffline = canReceive && deviceStatus === 'offline';
    // Idle 'keypad' tab is the default — any non-idle leftover ('transfer',
    // null) renders as Keypad, so the value never ambiguously leaks between
    // the idle tabs and the in-call sub-panels. A target-less 'historyDetail'
    // (defensive: should not occur) falls back to the History tab.
    const idleTab: 'keypad' | 'history' | 'contacts' =
        activePanel === 'history' || activePanel === 'contacts'
            ? activePanel
            : activePanel === 'historyDetail'
              ? 'history'
              : 'keypad';

    return (
        <div
            data-slot={CALL_WIDGET_SLOT}
            // Default anchor sits under the header launcher (h-16 header →
            // top-20 on desktop; the mobile header adds a second row → top-28).
            //
            // `pointer-events-auto` is what keeps the widget CLICKABLE while a
            // modal surface is open. A modal Radix layer (every Dialog, and the
            // contact drawer through vaul, whose `modal` defaults to true) sets
            // `document.body.style.pointerEvents = 'none'` and re-enables it only
            // inside its own layer. This widget is a sibling of those portals, so
            // it inherited `none` and every click on it — End Call included —
            // went nowhere. z-index never mattered; the events were the problem.
            className={cn(
                'pointer-events-auto fixed z-1000',
                !position && 'top-28 right-4 md:top-20',
            )}
            style={positionStyle}
        >
            <div
                data-slot="voice-widget-panel"
                // max-h keeps the widget on-screen however long a panel's
                // content grows (scrolling happens inside the panels); the
                // stored resize height is a MINIMUM and must be clamped below
                // that cap — CSS lets min-height beat max-height, so an
                // uncapped value would stretch the widget past the viewport.
                className="relative flex max-h-[calc(100dvh-8rem)] flex-col overflow-hidden rounded-[22px] border border-white/60 bg-white/75 shadow-[0_18px_50px_-12px_rgb(15_23_42/0.35),0_6px_16px_-8px_rgb(15_23_42/0.22)] ring-1 ring-white/40 ring-inset backdrop-blur-2xl backdrop-saturate-150 dark:border-white/10 dark:bg-neutral-900/70 dark:shadow-[0_24px_70px_-16px_rgb(0_0_0/0.75)] dark:ring-white/[0.08]"
                // Width is always set here rather than via a `w-80` class, so
                // the design width and the resized width share one source of
                // truth. `--voice-scale` lets the panels below grow their type
                // and controls in step instead of floating in dead space.
                style={
                    {
                        width: size?.width ?? WIDGET_BASE_WIDTH,
                        '--voice-scale': widgetScale(size),
                        ...(size
                            ? { minHeight: `min(${size.height}px, calc(100dvh - 8rem))` }
                            : {}),
                    } as React.CSSProperties
                }
            >
                <WidgetHeader
                    dragHandlers={headerDrag}
                    onMinimize={() => setMinimized(true)}
                    canReceive={canReceive}
                    canMake={canMake}
                />

                {voiceOffline && (
                    // A named reason means retrying cannot help (the org's voice
                    // config is wrong) — say so instead of implying progress.
                    <div
                        className={cn(
                            'flex items-start gap-1.5 border-b px-3 py-1.5 text-xs text-text-secondary',
                            deviceError
                                ? 'border-white/40 bg-error-500/10 dark:border-white/[0.06] dark:bg-error-500/[0.12]'
                                : 'border-white/40 bg-warning-500/10 dark:border-white/[0.06] dark:bg-warning-500/[0.12]',
                        )}
                    >
                        <span
                            className={cn(
                                'mt-1 size-1.5 shrink-0 rounded-full',
                                deviceError ? 'bg-error-500' : 'animate-pulse bg-warning-500',
                            )}
                        />
                        {deviceError ?? 'Voice offline — reconnecting…'}
                    </div>
                )}

                {dialBlocked && (
                    <div className="flex items-center gap-1.5 border-b border-white/40 bg-error-500/10 px-3 py-1.5 text-xs text-text-secondary dark:border-white/[0.06] dark:bg-error-500/[0.12]">
                        <span className="size-1.5 shrink-0 rounded-full bg-error-500" />
                        No voice minutes left — outbound calling is paused.
                    </div>
                )}

                {ringAudioBlocked && (
                    <div className="flex items-center gap-1.5 border-b border-white/40 bg-warning-500/10 px-3 py-1.5 text-xs text-text-secondary dark:border-white/[0.06] dark:bg-warning-500/[0.12]">
                        <span className="size-1.5 shrink-0 animate-pulse rounded-full bg-warning-500" />
                        Sound is blocked — click anywhere to hear ringing.
                    </div>
                )}

                {/* Ringing + hold queues sit atop the body in EVERY state. */}
                <RingingQueue
                    queue={ringingQueue}
                    canAnswer={!activeCall && !isDialing}
                    onAnswer={answerIncoming}
                    onDecline={declineIncoming}
                />
                <HeldQueue
                    queue={heldQueue}
                    canPick={!activeCall && !isDialing}
                    onPick={(callId) => void pickHeldCall(callId)}
                />

                {activeCall ? (
                    activePanel === 'keypad' ? (
                        <InCallKeypadPanel
                            onBack={() => setActivePanel(null)}
                            onPress={sendDigits}
                        />
                    ) : activePanel === 'transfer' ? (
                        <TypingFocusScope>
                            <TransferPanel
                                onBack={() => setActivePanel(null)}
                                onTransfer={handleTransfer}
                                isTransferring={transferMutation.isPending}
                            />
                        </TypingFocusScope>
                    ) : (
                        <InCallPanel
                            call={activeCall}
                            onToggleMute={toggleMute}
                            onToggleHold={handleToggleHold}
                            onOpenKeypad={() => setActivePanel('keypad')}
                            onOpenTransfer={() => setActivePanel('transfer')}
                            onEnd={handleEnd}
                            isHoldPending={holdMutation.isPending || resumeMutation.isPending}
                            isEndPending={endMutation.isPending}
                        />
                    )
                ) : postCallCardCallId ? (
                    <TypingFocusScope key={postCallCardCallId}>
                        <PostCallCard
                            callId={postCallCardCallId}
                            onCommit={(id, payload) =>
                                dispositionMutation.mutate({ id, ...payload })
                            }
                            isSaving={dispositionMutation.isPending}
                            onClose={() => setPostCallCardCallId(null)}
                            onCallAgain={(toNumber) => void handleDial(toNumber)}
                            canCallAgain={!isDialing}
                            onBlacklist={(phone) =>
                                blockMutation.mutate({
                                    phone,
                                    ...(postCallCardCallId
                                        ? { sourceCallId: postCallCardCallId }
                                        : {}),
                                })
                            }
                            isBlocking={blockMutation.isPending}
                        />
                    </TypingFocusScope>
                ) : activePanel === 'historyDetail' && historyDetailTarget ? (
                    <HistoryDetailPanel
                        target={historyDetailTarget}
                        onBack={closeHistoryDetail}
                        onCall={(toNumber, options) => void handleDial(toNumber, options)}
                        fill={size !== null}
                    />
                ) : (
                    <div className="flex min-h-0 flex-1 flex-col">
                        <div className="mx-3 mt-3 grid grid-cols-3 gap-1 rounded-full bg-black/[0.04] p-1 dark:bg-white/[0.05]">
                            {IDLE_TABS.map(({ id, label, Icon }) => (
                                <button
                                    key={id}
                                    type="button"
                                    onClick={() => setActivePanel(id)}
                                    className={cn(
                                        'flex items-center justify-center gap-1.5 rounded-full py-[calc(0.375rem*var(--voice-scale,1))] text-[calc(0.75rem*var(--voice-scale,1))] font-medium transition-all focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none',
                                        idleTab === id
                                            ? 'bg-white/90 text-text-primary shadow-sm dark:bg-white/[0.14]'
                                            : 'text-text-tertiary hover:text-text-secondary',
                                    )}
                                >
                                    <Icon className="size-[calc(0.875rem*var(--voice-scale,1))]" />
                                    {label}
                                </button>
                            ))}
                        </div>
                        {idleTab === 'history' ? (
                            <HistoryPanel
                                onRedial={(toNumber) => void handleDial(toNumber)}
                                onOpenDetail={openHistoryDetail}
                                fill={size !== null}
                            />
                        ) : idleTab === 'contacts' ? (
                            <ContactsPanel
                                onCall={(toNumber) => void handleDial(toNumber)}
                                fill={size !== null}
                            />
                        ) : (
                            <DialerPanel
                                onDial={(n) => void handleDial(n)}
                                isDialing={isDialing}
                                disabledReason={dialDisabledReason}
                                isEntryDisabled={noNumberBlocked}
                            />
                        )}
                    </div>
                )}

                {/* Resize grip — width/height limits live in useWidgetResize
                        (design size → +50% per axis); double-click restores the
                        design size. */}
                <div
                    {...resize}
                    role="button"
                    aria-label="Resize call widget"
                    title="Drag to resize · double-click to reset"
                    className="absolute right-0.5 bottom-0.5 flex size-5 cursor-nwse-resize touch-none items-end justify-end p-1 text-text-tertiary/70 select-none hover:text-text-secondary"
                >
                    <svg viewBox="0 0 8 8" className="size-2.5" aria-hidden="true">
                        <path
                            d="M7 1 1 7 M7 4.5 4.5 7"
                            stroke="currentColor"
                            strokeWidth="1.2"
                            strokeLinecap="round"
                            fill="none"
                        />
                    </svg>
                </div>
            </div>
        </div>
    );
}
